// Global state management via Preact context.
// Holds session, addresses, messages, toasts, and navigation state.

import { createContext } from 'preact';
import { useContext, useState, useCallback } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { Address, Label, Message, SentEmail, Proof } from './types';

// ─── Types ───

export type View = 'inbox' | 'sent' | 'aliases' | 'settings';

export interface Session {
  pubkey: string;
  secretKeyHex: string | null; // null when using NIP-07 extension
  authMethod: 'nsec' | 'extension';
}

export interface Toast {
  id: number;
  message: string;
  type: '' | 'error' | 'success';
}

interface Store {
  // Session
  session: Session | null;
  setSession: (s: Session | null) => void;

  // Data
  addresses: Address[];
  setAddresses: (a: Address[]) => void;
  messages: Message[];
  setMessages: (m: Message[]) => void;
  sentMail: SentEmail[];
  setSentMail: (s: SentEmail[]) => void;
  proofs: Proof[];
  setProofs: (p: Proof[]) => void;
  labels: Label[];
  setLabels: (l: Label[]) => void;
  activeLabel: number | null;
  setActiveLabel: (id: number | null) => void;

  // Navigation
  currentView: View;
  setCurrentView: (v: View) => void;
  currentMessageId: string | null;
  setCurrentMessageId: (id: string | null) => void;

  // Search
  searchQuery: string;
  setSearchQuery: (q: string) => void;

  // Compose
  composeOpen: boolean;
  setComposeOpen: (open: boolean) => void;

  // Toasts
  toasts: Toast[];
  showToast: (message: string, type?: '' | 'error' | 'success') => void;
  dismissToast: (id: number) => void;
}

// ─── Context ───

const StoreContext = createContext<Store | null>(null);

// ─── Provider ───

let toastCounter = 0;

export function StoreProvider({ children }: { children: ComponentChildren }) {
  const [session, setSession] = useState<Session | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [sentMail, setSentMail] = useState<SentEmail[]>([]);
  const [proofs, setProofs] = useState<Proof[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [activeLabel, setActiveLabel] = useState<number | null>(null);
  const [currentView, setCurrentView] = useState<View>('inbox');
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: '' | 'error' | 'success' = '') => {
    const id = ++toastCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const store: Store = {
    session,
    setSession,
    addresses,
    setAddresses,
    messages,
    setMessages,
    sentMail,
    setSentMail,
    proofs,
    setProofs,
    labels,
    setLabels,
    activeLabel,
    setActiveLabel,
    currentView,
    setCurrentView,
    currentMessageId,
    setCurrentMessageId,
    searchQuery,
    setSearchQuery,
    composeOpen,
    setComposeOpen,
    toasts,
    showToast,
    dismissToast,
  };

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

// ─── Hook ───

export function useStore(): Store {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}

// ─── Convenience hook for primary address ───

export function usePrimaryAddress(): Address | null {
  const { addresses } = useStore();
  if (!addresses || addresses.length === 0) return null;
  return addresses.find((a) => a.isPrimary) || addresses[0];
}
